import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Check, Zap, Crown, Rocket, Paintbrush } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Pricing = () => {
  const { user } = useAuth();
  const { isSubscribed } = useSubscription();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const plans = [
    {
      name: "Rainbow Rookie",
      price: "$0",
      period: "forever",
      credits: "12 credits",
      creditsValue: 12,
      icon: <Zap className="w-5 h-5" />,
      features: [
        "2 Skecha coloring pages",
        "Basic downloads",
        "Kid-friendly themes",
        "Community support"
      ],
      popular: false,
      variant: "outline" as const,
      isFree: true
    },
    {
      name: "Little Artist",
      price: "$4.95",
      period: "month",
      credits: "60 credits",
      creditsValue: 60,
      icon: <Paintbrush className="w-5 h-5" />,
      features: [
        "10 Skecha coloring pages",
        "High-quality downloads",
        "Kid-friendly themes",
        "Email support"
      ],
      popular: false,
      variant: "outline" as const
    },
    {
      name: "Creative Explorer", 
      price: "$9.95",
      period: "month",
      credits: "150 credits",
      creditsValue: 150,
      icon: <Crown className="w-5 h-5" />,
      features: [
        "25 Skecha coloring pages",
        "Photo-to-coloring theme magic",
        "HD downloads",
        "All premium themes",
        "Priority support"
      ],
      popular: true,
      variant: "hero" as const
    },
    {
      name: "Theme Master",
      price: "$19.95",
      period: "month",
      credits: "400 credits",
      creditsValue: 400,
      icon: <Rocket className="w-5 h-5" />,
      features: [
        "65 Skecha coloring pages",
        "Photo-to-coloring theme magic",
        "Ultra HD downloads",
        "Priority support",
        "Perfect for families"
      ],
      popular: false,
      variant: "creative" as const
    }
  ];

  const quickLoadPlan = {
    name: "Quick Load",
    price: "$2.95",
    period: "one-time",
    credits: "30 credits",
    creditsValue: 30,
    icon: <Zap className="w-5 h-5" />,
    features: [
      "5 Skecha coloring pages",
      "Instant delivery",
      "High-quality downloads",
      "Perfect for immediate needs"
    ],
    isQuickLoad: true
  };

  const handlePlanPurchase = async (plan: typeof plans[0] | typeof quickLoadPlan) => {
    // Handle free tier - direct to signup
    if ('isFree' in plan && plan.isFree) {
      navigate("/auth");
      return;
    }

    if (!user) {
      navigate("/auth");
      return;
    }

    setLoadingPlan(plan.name);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          credits: plan.creditsValue,
          isQuickLoad: 'isQuickLoad' in plan ? plan.isQuickLoad : false
        }
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        // Open Stripe checkout in a new tab
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirecting to Payment",
          description: `Opening Stripe checkout for ${plan.name}...`,
        });
      } else {
        throw new Error('No payment URL received');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to process payment: ${error.message}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="py-20 px-4 bg-gradient-subtle" data-testid="pricing-plans">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Simple, Credit-Based Pricing
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Pay only for what you create. Buy credits that never expire and use them whenever inspiration strikes.
          </p>
        </div>


        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <Card 
              key={index} 
              className={`relative ${plan.popular ? 'ring-2 ring-primary shadow-creative' : ''} hover:shadow-lg transition-all duration-300`}
              data-testid="pricing-plan"
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-hero text-primary-foreground">
                  Most Popular
                </Badge>
              )}
              <CardHeader className="text-center pb-6">
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  plan.popular ? 'bg-gradient-hero text-primary-foreground' : 'bg-accent text-accent-foreground'
                }`}>
                  {plan.icon}
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">/{plan.period}</span>
                </div>
                <p className="text-primary font-medium mb-6">{plan.credits}</p>
                <Button 
                  variant={plan.variant} 
                  className="w-full"
                  size="lg"
                  onClick={() => handlePlanPurchase(plan)}
                  disabled={loadingPlan !== null || isSubscribed || (user && !isSubscribed && plan.name === "Rainbow Rookie")}
                  data-gtm-event="subscription_checkout"
                  data-gtm-plan={plan.name.toLowerCase().replace(/\s+/g, '_')}
                  data-gtm-currency="USD"
                  data-gtm-value={parseFloat((plan.price || '').replace(/[^0-9.]/g, '')) || undefined}
                >
                  {loadingPlan === plan.name ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : isSubscribed ? (
                    "Already Subscribed"
                  ) : (user && !isSubscribed && plan.name === "Rainbow Rookie") ? (
                    "Trial Already Used"
                  ) : (
                    "Get Started"
                  )}
                </Button>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3" data-testid="plan-features">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden">
          <Carousel
            setApi={setApi}
            className="w-full pb-16"
            opts={{
              align: "start",
              loop: true,
            }}
          >
            <CarouselContent>
              {plans.map((plan, index) => (
                <CarouselItem key={index}>
                  <Card 
                    className={`relative ${plan.popular ? 'ring-2 ring-primary shadow-creative' : ''} hover:shadow-lg transition-all duration-300`}
                    data-testid="pricing-plan"
                  >
                    {plan.popular && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-hero text-primary-foreground">
                        Most Popular
                      </Badge>
                    )}
                    <CardHeader className="text-center pb-6">
                      <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-4 ${
                        plan.popular ? 'bg-gradient-hero text-primary-foreground' : 'bg-accent text-accent-foreground'
                      }`}>
                        {plan.icon}
                      </div>
                      <CardTitle className="text-2xl">{plan.name}</CardTitle>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">{plan.price}</span>
                        <span className="text-muted-foreground">/{plan.period}</span>
                      </div>
                      <p className="text-primary font-medium mb-6">{plan.credits}</p>
                      <Button 
                        variant={plan.variant} 
                        className="w-full"
                        size="lg"
                        onClick={() => handlePlanPurchase(plan)}
                        disabled={loadingPlan !== null || isSubscribed || (user && !isSubscribed && plan.name === "Rainbow Rookie")}
                        data-gtm-event="subscription_checkout"
                        data-gtm-plan={plan.name.toLowerCase().replace(/\s+/g, '_')}
                        data-gtm-currency="USD"
                        data-gtm-value={parseFloat((plan.price || '').replace(/[^0-9.]/g, '')) || undefined}
                      >
                        {loadingPlan === plan.name ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Processing...
                          </>
                        ) : isSubscribed ? (
                          "Already Subscribed"
                        ) : (user && !isSubscribed && plan.name === "Rainbow Rookie") ? (
                          "Trial Already Used"
                        ) : (
                          "Get Started"
                        )}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3" data-testid="plan-features">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center gap-3">
                            <Check className="w-4 h-4 text-primary flex-shrink-0" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>

            {/* Custom Navigation */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-3 z-20">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 rounded-full bg-muted/90 hover:bg-muted border-border/50 shadow-sm" />
              
              <div className="flex items-center gap-2">
                {Array.from({ length: count }).map((_, index) => (
                  <button
                    key={index}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === current 
                        ? 'w-8 bg-primary' 
                        : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    }`}
                    onClick={() => api?.scrollTo(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              
              <CarouselNext className="static translate-y-0 h-10 w-10 rounded-full bg-muted/90 hover:bg-muted border-border/50 shadow-sm" />
            </div>
          </Carousel>
        </div>

        {/* Quick Load Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-2">Need Credits Right Now?</h3>
            <p className="text-muted-foreground">Get instant access with our Quick Load option</p>
          </div>
          
          <Card className="max-w-sm mx-auto border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardHeader className="text-center pb-4">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-4">
                {quickLoadPlan.icon}
              </div>
              <CardTitle className="text-xl">{quickLoadPlan.name}</CardTitle>
              <div className="mt-2">
                <span className="text-3xl font-bold">{quickLoadPlan.price}</span>
                <span className="text-muted-foreground">/{quickLoadPlan.period}</span>
              </div>
              <p className="text-primary font-medium mb-4">{quickLoadPlan.credits}</p>
              <Button 
                variant="default"
                className="w-full"
                onClick={() => handlePlanPurchase(quickLoadPlan)}
                disabled={loadingPlan !== null || isSubscribed}
                data-gtm-event="credit_purchased"
                data-gtm-pack="quick_load_30"
                data-gtm-value="2.95"
                data-gtm-currency="USD"
              >
                {loadingPlan === quickLoadPlan.name ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                  ) : isSubscribed ? (
                    "Already Subscribed"
                  ) : (
                    "Quick Load Now"
                )}
              </Button>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {quickLoadPlan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Need more credits? Contact us for custom enterprise plans.
          </p>
          <Button variant="outline">
            Contact Sales
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Pricing;